import React from 'react';
import autoBind from 'react-autobind';
import IconButton from 'material-ui/IconButton';
import FlatButton from 'material-ui/FlatButton';
import PadLock from 'material-ui/svg-icons/action/lock';
import UnLock from 'material-ui/svg-icons/action/lock-open';
import ArrowDown from 'material-ui/svg-icons/hardware/keyboard-arrow-down';
import ArrowUp from 'material-ui/svg-icons/hardware/keyboard-arrow-up';
import Field from '../../../components/Field';
import noty from '../../../lib/noty';
import FieldImage from '../../../components/FieldImage';
import FieldSlider from '../../../components/FieldSlider';
import { PolyInstructions } from '../../../components/Instructions';
import { PanelContainer, Panel } from '../../../components/Panel';
import HelpToolTip from '../../../components/HelpToolTip';
import {
  MarkerPanel,
  withMarkerPanel,
  MarkerPanelMarkers
} from '../../../components/MarkerPanel';
import MapOverlay from '../../../components/MapOverlay';
import { MapPortal } from '../../../components/Map';
import MapEditOverlay from '../MapEditOverlay';
import {
  pointToBounds,
  boundsToPoint,
  scaleBounds
} from '../../../lib/google-maps';

class EditFloor extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      panelOpen: true,
      fetching: false,
      touched: false,
      floorPlanLocked: true,
      floor: this.props.floor
    };
    this.scaleTimer = null;
    this.scaleInterval = 120;
  }

  handlePanelToggle() {
    this.setState({ panelOpen: !this.state.panelOpen });
  }

  handleChange({ name, value }) {
    const path = name.split('.');
    this.setState({
      touched: true,
      floor: this.state.floor.setIn(path, value)
    });
  }

  handleImageChange({ name, value }) {
    this.setState({ fetching: true });
    // When updating an image, the coordinates for the
    // floor also need to be updated to reflect the aspect
    // ratio of the uploaded image's dimensions
    const { floor } = this.state;
    const image = new window.Image();
    image.onload = () => {
      const height = image.height;
      const width = image.width;
      // get the center point from the current corddinates polygon
      const center = boundsToPoint(floor.geo.coordinates);
      // create new coordinates at the new aspect ratio
      const coordinates = pointToBounds(center, 0.05, width / height);

      const path = name.split('.');
      let updatedFloor = floor.setIn(path, value);
      updatedFloor = updatedFloor.setIn(['geo', 'coordinates'], coordinates);
      this.setState({ floor: updatedFloor, fetching: false, touched: true });
    };

    if (value.path) {
      image.src = value.path;
    } else {
      const path = name.split('.');
      const updatedFloor = floor.setIn(path, value);
      this.setState({ floor: updatedFloor, fetching: false, touched: true });
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    const { floor } = this.state;
    this.setState({ fetching: true });
    floor
      .patch()
      .then(patched => {
        this.setState({ fetching: false, touched: false });
        noty.success('Floor updated.');
      })
      .catch(err => {
        this.setState({ fetching: false });
        noty.errors(err);
      });
  }

  handleRemove() {
    const { floor } = this.state;
    noty.confirm(`Are you sure you want to delete "${floor.name}"`, {
      onOk: () => {
        this.setState({ fetching: true });
        floor
          .remove()
          .then(removed => {
            noty.success('Floor removed.');
          })
          .catch(err => {
            this.setState({ fetching: false });
            noty.errors(err);
          });
      }
    });
  }

  handleImageLock() {
    this.setState({ floorPlanLocked: !this.state.floorPlanLocked });
  }

  handleImageScaleIncrease() {
    const coords = this.state.floor.getIn(['geo', 'coordinates']);
    const scaledCoords = scaleBounds(coords, 1.01);
    this.setState({
      floor: this.state.floor.setIn(['geo', 'coordinates'], scaledCoords)
    });
    this.scaleInterval *= 0.7;
    this.scaleTimer = window.setTimeout(
      this.handleImageScaleIncrease,
      this.scaleInterval
    );
  }

  handleImageScaleDecrease() {
    const coords = this.state.floor.getIn(['geo', 'coordinates']);
    const scaledCoords = scaleBounds(coords, 0.99);
    this.setState({
      floor: this.state.floor.setIn(['geo', 'coordinates'], scaledCoords)
    });
    this.scaleInterval *= 0.7;
    this.scaleTimer = window.setTimeout(
      this.handleImageScaleDecrease,
      this.scaleInterval
    );
  }

  stopScaling() {
    clearTimeout(this.scaleTimer);
    this.scaleTimer = null;
    this.scaleInterval = 120;
  }

  render() {
    const {
      markerFilter,
      toggleAllMarkerFilter,
      toggleMarkerFilter,
      match
    } = this.props;
    const { floor, fetching, touched, floorPlanLocked } = this.state;
    const markers = floor.markers();
    return (
      <div>
        <PanelContainer
          width={475}
          open={this.state.panelOpen}
          onToggle={() => this.handlePanelToggle()}
        >
          <Panel
            onSaveClick={this.handleSubmit}
            saveTitle="Save Floor"
            onDeleteClick={this.handleRemove}
            // onCopyClick={this.handleRemove}
            fetching={fetching}
            touched={touched}
            touchedMessage="Are you sure that you want to leave? This floor has unsaved changes."
          >
            <div>
              <div className="row">
                <div className="col-xs-12">
                  <Field
                    name="name"
                    value={floor.name}
                    onChange={this.handleChange}
                    hintText="First Floor"
                    label="Floor Name"
                    disabled={fetching}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <FieldImage
                    value={floor.overlay.image}
                    name="overlay.image"
                    onChange={this.handleImageChange}
                    label="Overlay Image"
                    disabled={fetching}
                    tooltip="Choose your floor plan image for this floor (transparent PNG files are best)"
                  />
                </div>
              </div>
              <div className="row floor-input-row">
                <div className="col-xs-6">
                  <FieldSlider
                    min={-180}
                    max={180}
                    step={1}
                    value={
                      // Note: in order to default the slider to middle
                      // coerce 0 - 360 degrees to -180 - 180.
                      floor.overlay.heading >= 0 && floor.overlay.heading <= 180
                        ? floor.overlay.heading
                        : (360 - floor.overlay.heading) * -1
                    }
                    name="overlay.heading"
                    label="Overlay Rotation"
                    onChange={({ name, value }) => {
                      // Coerce values back to 0 - 360 degrees.
                      const newValue =
                        value >= 0 ? value : 360 - Math.abs(value);
                      this.handleChange({ name, value: newValue });
                    }}
                    disabled={fetching}
                    tooltip="Use this slider to rotate your floor plan if needed"
                  />
                </div>
                <div className="col-xs-6">
                  <FieldSlider
                    min={0}
                    max={1}
                    step={0.05}
                    value={floor.overlay.opacity}
                    name="overlay.opacity"
                    label="Overlay Opacity"
                    onChange={this.handleChange}
                    disabled={fetching}
                    tooltip="Adjust this to see through your floor plan to make it easier to align on top of the building"
                  />
                </div>
              </div>
              <div className="row floor-input-row">
                <div className="col-xs-12">
                  <label className="field-label">
                    <HelpToolTip
                      tip="Adjust the image size"
                      style={{ position: 'absolute' }}
                    />
                    <span style={{ paddingLeft: 20 }}>Image Size</span>
                  </label>
                  <FlatButton
                    onMouseDown={this.handleImageScaleIncrease}
                    onMouseUp={this.stopScaling}
                    label="Increase Size"
                    icon={<ArrowUp />}
                    style={{ marginRight: 15 }}
                    backgroundColor="#ededed"
                    hoverColor="#dddddd"
                  />
                  <FlatButton
                    onMouseDown={this.handleImageScaleDecrease}
                    onMouseUp={this.stopScaling}
                    label="Decrease Size"
                    icon={<ArrowDown />}
                    backgroundColor="#ededed"
                    hoverColor="#dddddd"
                  />
                </div>
              </div>
              <PolyInstructions />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={this.handleImageLock}>
                  {floorPlanLocked ? (
                    <UnLock color="#999" />
                  ) : (
                    <PadLock color="#999" />
                  )}
                </IconButton>
                <span className="text-muted small">
                  {floorPlanLocked
                    ? 'to unlock the floor plan from the map'
                    : 'to lock the floor plan to the map'}
                </span>
              </div>
            </div>
          </Panel>
          <MarkerPanel
            match={match}
            floor={floor}
            toggleMarkerFilter={toggleMarkerFilter}
            toggleAllMarkerFilter={toggleAllMarkerFilter}
            markerFilter={markerFilter}
            disabled={fetching}
          />
        </PanelContainer>
        <MapPortal center={this.props.defaultCenter}>
          {floorPlanLocked ? (
            <div>
              <MapOverlay floor={floor} />
            </div>
          ) : (
            <div>
              <MapEditOverlay
                overlay={floor.overlay}
                bounds={floor.geo.coordinates}
                onBoundsChange={coords =>
                  this.handleChange({
                    name: 'geo.coordinates',
                    value: coords
                  })
                }
              />
            </div>
          )}
          <MarkerPanelMarkers
            match={match}
            markers={markers}
            markerFilter={markerFilter}
            floor={floor}
          />
        </MapPortal>
      </div>
    );
  }
}

export default withMarkerPanel(EditFloor);



// WEBPACK FOOTER //
// ./src/pages/Facility/EditFloor/index.jsx